import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Button,
	Col,
	Container,
	FormGroup,
	Input,
	Label,
	Row,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import indicadoresService from "services/IndicadoresService";
import Launch, { years } from "./Launch";
import Multiple from "components/widgets/multiple";
import organizationalStructureService from "services/OrganizationalStructureService";
import permissionsService from "services/permissionsService";
import Loading from "components/Common/Loading";
import "./index.css";
import Swal from "sweetalert2";
import { Funnel, FloppyDisk, SignOut, Lock, Pencil, Trash } from "@phosphor-icons/react";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import { useLocation, useNavigate } from "react-router-dom";
import Spreadsheet from "react-spreadsheet";
import Select from "react-select";
import { IndicadorFormContent } from "./Form";
import { buildSpreadsheetCopyTsv } from "helpers/spreadsheetClipboardHelper";


const VARIABLE_QUANTIFIER_OPTIONS = [
	{ value: "", label: "Nenhum" },
	{ value: "R$", label: "R$" },
	{ value: "%", label: "%" },
	{ value: "x", label: "x" },
	{ value: "h", label: "h" },
	{ value: "Un", label: "Un" },
	{ value: "pts", label: "pts" },
];

const VARIABLE_PERIODICITY_OPTIONS = [
	{ value: "Mensal", label: "Mensal (12 períodos)" },
	{ value: "Bimestral", label: "Bimestral (6 períodos – a cada 2 meses)" },
	{ value: "Trimestral", label: "Trimestral (4 períodos – a cada 3 meses)" },
	{
		value: "Quadrimestral",
		label: "Quadrimestral (3 períodos – a cada 4 meses)",
	},
	{ value: "Semestral", label: "Semestral (2 períodos – a cada 6 meses)" },
	{ value: "Anual", label: "Anual (1 período – 12 meses)" },
];

const VARIABLE_DECIMALS_OPTIONS = [
	{ value: 0, label: "0 (inteiro)" },
	{ value: 1, label: "1" },
	{ value: 2, label: "2" },
	{ value: 3, label: "3" },
	{ value: 4, label: "4" },
	{ value: 5, label: "5" },
	{ value: 6, label: "6" },
];

const DEFAULT_VARIABLE_DECIMALS = VARIABLE_DECIMALS_OPTIONS[2];

const VARIABLE_SORT_OPTIONS = [
	{ value: "name_asc", label: "Nome (A → Z)" },
	{ value: "name_desc", label: "Nome (Z → A)" },
	{ value: "periodicity", label: "Periodicidade" },
	{ value: "quantifier", label: "Quantificador" },
	{ value: "recent", label: "Mais recentes" },
];

const PERIODICITY_SORT_ORDER = [
	"Mensal",
	"Bimestral",
	"Trimestral",
	"Quadrimestral",
	"Semestral",
	"Anual",
];

const compareText = (a, b) =>
	String(a || "").localeCompare(String(b || ""), "pt-BR", {
		sensitivity: "base",
		numeric: true,
	});

const sortVariablesList = (list, sortBy) => {
	const items = [...(list || [])];
	switch (sortBy) {
		case "name_desc":
			return items.sort((a, b) => compareText(b.name, a.name));
		case "periodicity":
			return items.sort((a, b) => {
				const ai = PERIODICITY_SORT_ORDER.indexOf(a.periodicity);
				const bi = PERIODICITY_SORT_ORDER.indexOf(b.periodicity);
				const aOrder = ai === -1 ? 999 : ai;
				const bOrder = bi === -1 ? 999 : bi;
				if (aOrder !== bOrder) return aOrder - bOrder;
				return compareText(a.name, b.name);
			});
		case "quantifier":
			return items.sort((a, b) => {
				const byQuantifier = compareText(
					a.quantifier || "",
					b.quantifier || "",
				);
				if (byQuantifier !== 0) return byQuantifier;
				return compareText(a.name, b.name);
			});
		case "recent":
			return items.sort(
				(a, b) => Number(b.id || 0) - Number(a.id || 0),
			);
		case "name_asc":
		default:
			return items.sort((a, b) => compareText(a.name, b.name));
	}
};

const buildVariableUsageHtml = (usage, { mode, variableName }) => {
	const indicators = usage?.indicators || [];
	const formulas = usage?.formulas || [];
	const valuesCount = usage?.values_count || 0;
	const isInUse = usage?.is_in_use;

	const usageByIndicator = {};

	for (const i of indicators) {
		const key = String(i.id);
		usageByIndicator[key] = {
			id: i.id,
			name: i.name || `Indicador #${i.id}`,
			imported: true,
			formulas: [],
		};
	}

	for (const f of formulas) {
		const key = String(f.indicator_id ?? f.indicator_name ?? "unknown");
		if (!usageByIndicator[key]) {
			usageByIndicator[key] = {
				id: f.indicator_id,
				name: f.indicator_name || `Indicador #${f.indicator_id}`,
				imported: false,
				formulas: [],
			};
		}
		usageByIndicator[key].formulas.push({
			name: f.calculated_variable_name || "Variável calculada",
			formula: f.formula || null,
		});
	}

	const cardStyle =
		"border:1px solid #e5e7eb;border-radius:8px;padding:10px 12px;margin-top:8px;text-align:left;background:#fafafa;";
	const labelStyle =
		"font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:0.02em;margin-bottom:2px;";
	const titleStyle = "font-size:14px;font-weight:600;color:#111827;";
	const badgeStyle =
		"display:inline-block;font-size:11px;font-weight:500;padding:2px 8px;border-radius:999px;margin-right:6px;margin-top:6px;";
	const badgeImportedStyle = `${badgeStyle}background:rgba(193,143,0,0.12);color:#C18F00;`;
	const badgeFormulaStyle = `${badgeStyle}background:rgba(193,143,0,0.12);color:#C18F00;`;
	const itemStyle =
		"margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;";

	const usageEntries = Object.values(usageByIndicator);
	const indicatorsListStyle =
		"max-height:min(40vh,320px);overflow-y:auto;overflow-x:hidden;padding-right:4px;-webkit-overflow-scrolling:touch;";
	const indicatorsHtml =
		usageEntries.length > 0
			? `<div style="margin-top:4px;text-align:left;">
					<div style="${labelStyle}">Indicadores que usam esta variável</div>
					<div style="${indicatorsListStyle}">
					${usageEntries
						.map((group) => {
							const badges = [
								group.imported
									? `<span style="${badgeImportedStyle}">Adicionada ao indicador</span>`
									: "",
								group.formulas.length > 0
									? `<span style="${badgeFormulaStyle}">Em variável calculada</span>`
									: "",
							].join("");

							const formulasBlock =
								group.formulas.length > 0
									? group.formulas
											.map(
												(item) =>
													`<div style="${itemStyle}">
														<div style="${labelStyle}">Variável calculada</div>
														<div style="font-size:13px;font-weight:500;color:#111827;">${item.name}</div>
														${
															item.formula
																? `<div style="margin-top:4px;font-size:12px;color:#6b7280;font-family:monospace;">${item.formula}</div>`
																: ""
														}
													</div>`,
											)
											.join("")
									: "";

							return `<div style="${cardStyle}">
								<div style="${titleStyle}">${group.name}</div>
								<div>${badges}</div>
								${formulasBlock}
							</div>`;
						})
						.join("")}
					</div>
				</div>`
			: "";

	const introStyle =
		"margin:0 0 4px;text-align:left;font-size:14px;color:#374151;line-height:1.45;";
	const noteBoxStyle =
		"margin-top:12px;text-align:left;border-left:3px solid #C18F00;background:rgba(193,143,0,0.08);border-radius:0 8px 8px 0;padding:10px 12px;font-size:13px;color:#4b5563;line-height:1.5;";
	const valuesBoxStyle =
		"margin-top:10px;text-align:left;border:1px solid #e5e7eb;border-radius:8px;padding:8px 12px;background:#fff;font-size:13px;color:#374151;";
	const confirmStyle =
		"margin:14px 0 0;text-align:left;font-size:14px;font-weight:600;color:#111827;";

	const buildValuesHtml = (message) =>
		valuesCount > 0
			? `<div style="${valuesBoxStyle}">${message}</div>`
			: "";

	if (mode === "edit") {
		return `
			<p style="${introStyle}">Esta variável está em uso. Confira onde ela aparece antes de continuar:</p>
			${indicatorsHtml}
			${buildValuesHtml(
				`<strong style="color:#C18F00;">${valuesCount}</strong> valor(es) já lançado(s) para esta variável.`,
			)}
			<div style="${noteBoxStyle}">
				<div style="margin-bottom:6px;"><strong style="color:#C18F00;">Atenção</strong></div>
				<div>Ao alterar o <strong>nome</strong>, os lugares que a usam em fórmulas (variáveis calculadas) serão <strong>atualizados automaticamente</strong> com o novo nome.</div>
				<div style="margin-top:4px;">Alterar a <strong>periodicidade</strong> muda os meses disponíveis para lançamento.</div>
			</div>
			<p style="${confirmStyle}">Deseja continuar e editar?</p>
		`;
	}

	if (!isInUse) {
		return `<p style="${introStyle}">Tem certeza que deseja excluir a variável <strong>${variableName}</strong>?</p>`;
	}

	return `
		<p style="${introStyle}">Esta variável está em uso. Confira onde ela aparece antes de excluir:</p>
		${indicatorsHtml}
		${buildValuesHtml(
			`<strong style="color:#C18F00;">${valuesCount}</strong> valor(es) já lançado(s) serão apagados.`,
		)}
		<div style="${noteBoxStyle}">
			<div style="margin-bottom:6px;"><strong style="color:#C18F00;">Atenção</strong></div>
			<div>Ao excluir, ela será removida dos indicadores acima e poderá quebrar variáveis calculadas que a referenciam pelo nome.</div>
		</div>
		<p style="${confirmStyle}">Deseja realmente excluir?</p>
	`;
};

const LockedCellViewer = () => (
	<div
		style={{
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			height: "100%",
		}}
	>
		<Lock size={14} weight="bold" />
	</div>
);

const customStyles = {
	option: (provided, state) => ({
		...provided,
		color: state.isSelected ? "blue" : "black",
		backgroundColor: "white",
		"&:hover": {
			backgroundColor: "#556ee6",
		},
	}),
};

const NumericDataViewer = ({ cell }) => {
	const value = cell.value;
	const decimals = cell.decimals ?? 2;
	if (value === "" || value == null) return <span />;
	const isNegative = value.toString().startsWith("-");
	const cleanedValue = value.toString().replace(/[^\d]/g, "");
	if (cleanedValue === "") return <span>{isNegative ? "-" : ""}</span>;
	if (decimals === 0) {
		const numValue = parseInt(cleanedValue, 10) || 0;
		const formatted = numValue.toLocaleString("pt-BR");
		return <span style={{ textAlign: "right", display: "block" }}>{isNegative ? "-" : ""}{formatted}</span>;
	}
	const intPart = cleanedValue.slice(0, -decimals) || "0";
	const decimalPart = cleanedValue.slice(-decimals).padStart(decimals, "0");
	const numValue = parseFloat(intPart + "." + decimalPart);
	const formatted = numValue.toLocaleString("pt-BR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
	return <span style={{ textAlign: "right", display: "block" }}>{isNegative ? "-" : ""}{formatted}</span>;
};

const NumericDataEditor = ({ cell, onChange }) => {
	const [localValue, setLocalValue] = React.useState("");
	const decimals = cell.decimals ?? 2;
	React.useEffect(() => {
		const value = cell.value;
		if (value !== "" && value != null && value !== undefined) {
			const isNegative = value.toString().startsWith("-");
			const cleanedValue = value.toString().replace(/[^\d]/g, "");
			if (cleanedValue !== "") {
				if (decimals === 0) {
					const numValue = parseInt(cleanedValue, 10) || 0;
					setLocalValue((isNegative ? "-" : "") + numValue.toLocaleString("pt-BR"));
				} else {
					const intPart = cleanedValue.slice(0, -decimals) || "0";
					const decimalPart = cleanedValue.slice(-decimals).padStart(decimals, "0");
					const numValue = parseFloat(intPart + "." + decimalPart);
					setLocalValue((isNegative ? "-" : "") + numValue.toLocaleString("pt-BR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }));
				}
				return;
			}
		}
		setLocalValue("");
	}, [cell.value, decimals]);
	const handleChange = (e) => {
		const value = e.target.value;
		const isNegative = value.startsWith("-");
		const rawValue = value.replace(/[^\d-]/g, "").replace(/(?!^-)-/g, "");
		if (rawValue === "-") {
			setLocalValue("-");
			onChange({ ...cell, value: "" });
			return;
		}
		const numericString = rawValue.replace("-", "");
		if (numericString === "") {
			setLocalValue("");
			onChange({ ...cell, value: "" });
			return;
		}
		if (decimals === 0) {
			const numValue = parseInt(numericString, 10) || 0;
			setLocalValue((isNegative ? "-" : "") + numValue.toLocaleString("pt-BR"));
		} else {
			const intPart = numericString.slice(0, -decimals) || "0";
			const decimalPart = numericString.slice(-decimals).padStart(decimals, "0");
			const numValue = parseFloat(intPart + "." + decimalPart);
			setLocalValue((isNegative ? "-" : "") + numValue.toLocaleString("pt-BR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }));
		}
		const numericValue = parseInt(numericString, 10) * (isNegative ? -1 : 1);
		onChange({ ...cell, value: numericValue });
	};
	return (
		<input
			type="text"
			value={localValue}
			onChange={handleChange}
			autoFocus
			className="Spreadsheet__data-editor"
			style={{ textAlign: "right" }}
		/>
	);
};

const VariableCellViewer = ({ cell }) => {
	const isEmpty = cell.value === "" || cell.value == null;
	const placeholder = cell.placeholder ?? "0";
	if (isEmpty) {
		return <span style={{ color: "#9ca3af", textAlign: "center", display: "block" }}>{placeholder}</span>;
	}
	return <NumericDataViewer cell={cell} />;
};

const Indicadores = (props) => {
	document.title = "Senda | Indicadores";

	const location = useLocation();
	const navigate = useNavigate();
	const [isV2Enabled, setIsV2Enabled] = useState(false);
	const [dashboards, setDashboards] = useState([]);
	const [organizationStructures, setOrganizationStructures] = useState([]);
	const [permissions, setPermissions] = useState([]);

	const [openModal, setOpenModal] = useState(false);
	const [currentLaunchId, setCurrentLaunchId] = useState();

	const [currentOrganizationStructure, setCurrentOrganizationStructure] =
		useState();
	const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
	const [valueName, setValueName] = useState("");
	const [temPermissaoAdicionar, setTemPermissaoAdicionar] = useState(false);
	const [temPermissaoLancar, setTemPermissaoLancar] = useState(false);
	const [temPermissaoEditar, setTemPermissaoEditar] = useState(false);
	const [temPermissaoVisualizar, setTemPermissaoVisualizar] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isLoading2, setIsLoading2] = useState(false);

	const [isVariablesModalOpen, setIsVariablesModalOpen] = useState(false);
	const [isVariablesModalLoading, setIsVariablesModalLoading] =
		useState(false);
	const [isCreateVariableModalOpen, setIsCreateVariableModalOpen] =
		useState(false);
	const [isCreateVariableSaving, setIsCreateVariableSaving] = useState(false);
	const [editingVariable, setEditingVariable] = useState(null);
	const [createVariableName, setCreateVariableName] = useState("");
	const [createVariableQuantifier, setCreateVariableQuantifier] =
		useState(null);
	const [createVariablePeriodicity, setCreateVariablePeriodicity] =
		useState(null);
	const [createVariableDecimals, setCreateVariableDecimals] = useState(
		DEFAULT_VARIABLE_DECIMALS,
	);
	const [variables, setVariables] = useState([]);
	const [variablesSortBy, setVariablesSortBy] = useState("name_asc");

	const [selectedVariableIds, setSelectedVariableIds] = useState([]);

	const sortedVariables = useMemo(
		() => sortVariablesList(variables, variablesSortBy),
		[variables, variablesSortBy],
	);

	const [variablesData, setVariablesData] = useState({});

	const [viewMode, setViewMode] = useState(6);
	const [message, setMessage] = useState(null);

	const [isIndicadorModalOpen, setIsIndicadorModalOpen] = useState(false);
	const [editingIndicadorId, setEditingIndicadorId] = useState(null);

	const [isStructureLoaded, setIsStructureLoaded] = useState(false);
	
	// AbortController para cancelar requisições duplicadas
	const [abortController, setAbortController] = useState(null);
	
	// Estado para debounce do botão filtrar
	const [isFiltering, setIsFiltering] = useState(false);

	const variablesSpreadsheetRef = useRef(null);
	const variablesSelectionRef = useRef(null);
	const variablesSpreadsheetDataRef = useRef([]);
	const variablesColumnLabelsRef = useRef([]);
	const variablesRowLabelsRef = useRef([]);

	// Aplicar filtros vindos do state (quando voltando de outra página)
	useEffect(() => {
		const filters = location.state?.filters;
		
		if (filters) {
			setValueName(filters.name || "");
			setCurrentOrganizationStructure(filters.organization_id || "");
			setCurrentYear(filters.year || new Date().getFullYear());
			
			// Fazer a requisição apenas se a estrutura já foi carregada
			if (isStructureLoaded) {
				handleFilterInfos();
			}
		}
	}, [location.state, isStructureLoaded]);

	const toggleViewMode = (viewMode, setViewMode) => {
		const newViewMode = viewMode === 6 ? 12 : 6;
		setViewMode(newViewMode);

		SendaCustomSwal.fire({
			toast: true,
			position: "top-end",
			iconHtml:
				'<i class="info-icon" style="width: 16px; height: 16px;"></i>',
			title:
				newViewMode === 12
					? "Visualização alterada para vertical"
					: "Visualização alterada para duas por linha",
			showConfirmButton: false,
			timer: 3000,
			timerProgressBar: true,
		});
	};

	const getItemsDashboard = async (params) => {
		try {
			// Cancelar requisição anterior se existir
			if (abortController) {
				abortController.abort();
			}
			
			const newController = new AbortController();
			setAbortController(newController);
			
			setIsLoading2(true);
			setDashboards([]); // Limpar lista antes de buscar novos dados
			
			const response = await organizationalStructureService.getDashboard({
				...params,
				signal: newController.signal
			});
			setDashboards(response);
			setIsLoading2(false);
		} catch (error) {
			if (error.name !== 'AbortError') {
				console.log(error);
			}
			setIsLoading2(false);
		}
	};

	const getStrutureOrg = async () => {
		try {
			const response = await organizationalStructureService.get();

			const options = response.map((item) => ({
				value: item.id,
				label: item.name,
			}));

			setOrganizationStructures(options);

			// Só definir o departamento pai se não houver filtro do state
			const hasFilterFromState = location.state?.filters?.organization_id;
			
			if (!hasFilterFromState) {
				// Encontrar o departamento pai
				const rootOption = response.find((item) => item.parent_id === null);

				// Definir o pai de todos como valor inicial do select
				if (rootOption) {
					setCurrentOrganizationStructure(rootOption.id);
				}
			}

			setIsStructureLoaded(true);
		} catch (error) {
			console.log(error);
		}
	};

	const getPermissions = async () => {
		try {
			const response = await permissionsService.getPermissions();
			for (let permission of response) {
				if (permission.role === "GoalList") {
					setTemPermissaoVisualizar(true);
				}
				if (permission.role === "orgCreate") {
					setTemPermissaoAdicionar(true);
				}
				if (permission.role === "GoalLaunchData") {
					setTemPermissaoLancar(true);
				}
				if (permission.role === "GoalUpdate") {
					setTemPermissaoEditar(true);
				}
			}

			setPermissions(response);
		} catch (error) {
			console.log(error);
		}
	};

	const getV2Enabled = async () => {
		try {
			const response = await indicadoresService.getV2Enabled();
			setIsV2Enabled(!!response?.enabled);
		} catch (error) {
			console.log(error);
			setIsV2Enabled(false);
		}
	};

	const getAll = async () => {
		setIsLoading(true);
		await getPermissions();
		await getV2Enabled();
		await getStrutureOrg();
		setIsLoading(false);
	};

	useEffect(() => {
		const loadFilterInfos = async () => {
			if (isStructureLoaded && currentOrganizationStructure !== null) {
				await handleFilterInfos();
			}
		};

		loadFilterInfos();
	}, [isStructureLoaded]);

	useEffect(() => {
		getAll();
	}, []);

	// Cleanup do AbortController ao desmontar o componente
	useEffect(() => {
		return () => {
			if (abortController) {
				abortController.abort();
			}
		};
	}, [abortController]);

	const handleCloseLauch = () => {
		setOpenModal(false);
		setCurrentLaunchId();
	};

	const handleOpenLaunch = (id) => {
		setCurrentLaunchId(id);
		setOpenModal(true);
	};

	const handleFilterInfos = async () => {
		if (isFiltering || isLoading2) return; // Previne múltiplas execuções
		
		setIsFiltering(true);
		
		await getItemsDashboard({
			year: currentYear,
			organization_id: currentOrganizationStructure,
			name: valueName,
		});
		
		setIsFiltering(false);
	};

	const handleOpenIndicadorModal = (id = null) => {
		setEditingIndicadorId(id);
		setIsIndicadorModalOpen(true);
	};

	const handleCloseIndicadorModal = () => {
		setIsIndicadorModalOpen(false);
		setEditingIndicadorId(null);
	};

	const handleIndicadorSaved = () => {
		getItemsDashboard({
			year: currentYear,
			organization_id: currentOrganizationStructure,
			name: valueName,
		});
	};

	const refreshDashboard = () => {
		getItemsDashboard({
			year: currentYear,
			organization_id: currentOrganizationStructure,
			name: valueName,
		});
	};

	const handleOpenVariablesModal = async () => {
		setIsVariablesModalOpen(true);
		setIsVariablesModalLoading(true);
		try {
			// Buscar catálogo de variáveis e valores já lançados para o ano
			const [variablesResponse, valuesResponse] = await Promise.all([
				indicadoresService.getVariables(),
				indicadoresService.getVariableLaunch(currentYear),
			]);

			const vars = variablesResponse || [];
			setVariables(vars);

			// Montar mapa direto de valores por período (month = índice do período)
			const periodData = {};
			(valuesResponse || []).forEach((variable) => {
				if (variable.values && variable.values.length > 0) {
					const decimals = getDecimalsForVariable(variable);
					if (!periodData[variable.id]) {
						periodData[variable.id] = {};
					}
					variable.values.forEach((entry) => {
						const num = parseFloat(entry.value || "0");
						const raw = Math.round(num * Math.pow(10, decimals));
						periodData[variable.id][entry.month] = raw;
					});
				}
			});

			setVariablesData(periodData);
		} catch (error) {
			console.error("Erro ao buscar variáveis:", error);
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Erro",
				text: "Não foi possível carregar as variáveis.",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} finally {
			setIsVariablesModalLoading(false);
		}
	};

	const handleCloseVariablesModal = () => {
		setIsVariablesModalOpen(false);
	};

	const handleOpenCreateVariableModal = () => {
		setEditingVariable(null);
		setCreateVariableName("");
		setCreateVariableQuantifier(null);
		setCreateVariablePeriodicity(null);
		setCreateVariableDecimals(DEFAULT_VARIABLE_DECIMALS);
		setIsCreateVariableModalOpen(true);
	};

	const openEditVariableModal = (variable) => {
		setEditingVariable(variable);
		setCreateVariableName(variable.name || "");
		setCreateVariableQuantifier(
			VARIABLE_QUANTIFIER_OPTIONS.find(
				(opt) => opt.value === (variable.quantifier || ""),
			) || VARIABLE_QUANTIFIER_OPTIONS[0],
		);
		setCreateVariablePeriodicity(
			VARIABLE_PERIODICITY_OPTIONS.find(
				(opt) => opt.value === variable.periodicity,
			) || null,
		);
		const decimalsValue =
			variable.decimals_cases != null ? Number(variable.decimals_cases) : 2;
		setCreateVariableDecimals(
			VARIABLE_DECIMALS_OPTIONS.find((opt) => opt.value === decimalsValue) ||
				DEFAULT_VARIABLE_DECIMALS,
		);
		setIsCreateVariableModalOpen(true);
	};

	const handleCloseCreateVariableModal = () => {
		setIsCreateVariableModalOpen(false);
		setEditingVariable(null);
		setCreateVariableName("");
		setCreateVariableQuantifier(null);
		setCreateVariablePeriodicity(null);
		setCreateVariableDecimals(DEFAULT_VARIABLE_DECIMALS);
	};

	const toggleVariableSelection = (variableId) => {
		setSelectedVariableIds((prevSelected) =>
			prevSelected.includes(variableId)
				? prevSelected.filter((id) => id !== variableId)
				: [...prevSelected, variableId],
		);
	};

	const removeVariableFromLocalState = (variableId) => {
		setVariables((prev) => prev.filter((v) => v.id !== variableId));
		setSelectedVariableIds((prev) => prev.filter((id) => id !== variableId));
		setVariablesData((prev) => {
			if (!prev[variableId]) return prev;
			const next = { ...prev };
			delete next[variableId];
			return next;
		});
	};

	const handleEditVariable = async (variable) => {
		try {
			const usage = await indicadoresService.getVariableUsage(variable.id);

			if (usage?.is_in_use) {
				const result = await SendaCustomSwal.fire({
					iconHtml: '<i class="warning-icon"></i>',
					title: `Editar variável: ${variable.name}`,
					html: buildVariableUsageHtml(usage, {
						mode: "edit",
						variableName: variable.name,
					}),
					showCancelButton: true,
					confirmButtonText: "Sim, editar",
					cancelButtonText: "Cancelar",
					width: 560,
					customClass: {
						popup: "variable-usage-swal",
						confirmButton: "custom-confirm-button",
						cancelButton: "custom-cancel-button",
					},
				});

				if (!result.isConfirmed) return;
			}

			openEditVariableModal(variable);
		} catch (error) {
			console.error("Erro ao verificar uso da variável:", error);
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Erro",
				text: "Não foi possível verificar o uso da variável.",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		}
	};

	const handleDeleteVariable = async (variable) => {
		try {
			const usage = await indicadoresService.getVariableUsage(variable.id);

			const result = await SendaCustomSwal.fire({
				iconHtml: '<i class="warning-icon"></i>',
				title: "Excluir variável",
				html: buildVariableUsageHtml(usage, {
					mode: "delete",
					variableName: variable.name,
				}),
				showCancelButton: true,
				confirmButtonText: "Sim, excluir",
				cancelButtonText: "Cancelar",
				width: 560,
				customClass: {
					popup: "variable-usage-swal",
					confirmButton: "custom-confirm-button",
					cancelButton: "custom-cancel-button",
				},
			});

			if (!result.isConfirmed) return;

			await indicadoresService.deleteVariable(variable.id);
			removeVariableFromLocalState(variable.id);
			refreshDashboard();

			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Variável excluída com sucesso!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} catch (error) {
			console.error("Erro ao excluir variável:", error);
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Erro",
				text: "Não foi possível excluir a variável.",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		}
	};

	const selectedVariables = variables.filter((variable) =>
		selectedVariableIds.includes(variable.id),
	);

	const isMonthEnabledForVariable = (variable, monthId) => {
		const periodicity = (variable.periodicity || "").toLowerCase();

		switch (periodicity) {
			case "mensal":
				// Todos os 12 meses
				return true;
			case "bimestral":
				// 6 períodos – meses ímpares: 1,3,5,7,9,11
				return monthId % 2 === 1;
			case "trimestral":
				// 4 períodos – 1,4,7,10
				return (monthId - 1) % 3 === 0;
			case "quadrimestral":
				// 3 períodos – 1,5,9
				return (monthId - 1) % 4 === 0;
			case "semestral":
				// 2 períodos – 1,7
				return (monthId - 1) % 6 === 0;
			case "anual":
				// 1 período – apenas janeiro representa o ano
				return monthId === 1;
			default:
				// Qualquer outra configuração: habilita todos
				return true;
		}
	};

	const periodRows = [
		{ id: 1, label: "Jan" },
		{ id: 2, label: "Fev" },
		{ id: 3, label: "Mar" },
		{ id: 4, label: "Abr" },
		{ id: 5, label: "Mai" },
		{ id: 6, label: "Jun" },
		{ id: 7, label: "Jul" },
		{ id: 8, label: "Ago" },
		{ id: 9, label: "Set" },
		{ id: 10, label: "Out" },
		{ id: 11, label: "Nov" },
		{ id: 12, label: "Dez" },
	];

	const getPeriodPlaceholder = (variable, periodId) => {
		const p = (variable.periodicity || "").toLowerCase();
		switch (p) {
			case "mensal":
				return periodRows.find((r) => r.id === periodId)?.label ?? "";
			case "bimestral":
				return {
					1: "Jan-Fev", 3: "Mar-Abr", 5: "Mai-Jun",
					7: "Jul-Ago", 9: "Set-Out", 11: "Nov-Dez",
				}[periodId] ?? "";
			case "trimestral":
				return {
					1: "Jan-Mar", 4: "Abr-Jun", 7: "Jul-Set", 10: "Out-Dez",
				}[periodId] ?? "";
			case "quadrimestral":
				return {
					1: "Jan-Abr", 5: "Mai-Ago", 9: "Set-Dez",
				}[periodId] ?? "";
			case "semestral":
				return { 1: "Jan-Jun", 7: "Jul-Dez" }[periodId] ?? "";
			case "anual":
				return periodId === 1 ? "Jan-Dez" : "";
			default:
				return periodRows.find((r) => r.id === periodId)?.label ?? "";
		}
	};

	const getDecimalsForVariable = (variable) => {
		const parsed = Number(variable?.decimals_cases);
		if (Number.isNaN(parsed)) return 2;
		return Math.min(6, Math.max(0, parsed));
	};

	const columnLabelsVariables = selectedVariables.map((variable) => {
		const unitLabel = variable.quantifier || "Nenhum";
		return `${variable.name}\n${unitLabel} • ${variable.periodicity}`;
	});

	const rowLabelsVariables = periodRows.map((period) => period.label);

	const spreadsheetDataVariables = periodRows.map((period) =>
		selectedVariables.map((variable) => {
			const enabled = isMonthEnabledForVariable(variable, period.id);
			const value =
				variablesData[variable.id] &&
				variablesData[variable.id][period.id];

			if (!enabled) {
				return {
					value: "",
					readOnly: true,
					DataViewer: LockedCellViewer,
				};
			}

			return {
				value: value || "",
				placeholder: getPeriodPlaceholder(variable, period.id),
				decimals: getDecimalsForVariable(variable),
				DataViewer: VariableCellViewer,
				DataEditor: NumericDataEditor,
			};
		}),
	);

	variablesSpreadsheetDataRef.current = spreadsheetDataVariables;
	variablesColumnLabelsRef.current = columnLabelsVariables;
	variablesRowLabelsRef.current = rowLabelsVariables;

	const handleVariablesSelect = useCallback((selected) => {
		variablesSelectionRef.current = selected;
	}, []);

	useEffect(() => {
		if (!isVariablesModalOpen) return undefined;

		const handleCopy = (event) => {
			const root = variablesSpreadsheetRef.current;
			if (!root || !root.matches(":focus-within")) return;

			const tsv = buildSpreadsheetCopyTsv({
				data: variablesSpreadsheetDataRef.current,
				selection: variablesSelectionRef.current,
				columnLabels: variablesColumnLabelsRef.current,
				rowLabels: variablesRowLabelsRef.current,
			});
			if (!tsv) return;

			event.preventDefault();
			event.stopImmediatePropagation();
			event.clipboardData?.setData("text/plain", tsv);
		};

		document.addEventListener("copy", handleCopy, true);
		return () => document.removeEventListener("copy", handleCopy, true);
	}, [isVariablesModalOpen]);

	const handleSpreadsheetVariablesChange = (newData) => {
		const updatedData = { ...variablesData };

		newData.forEach((row, periodIndex) => {
			const period = periodRows[periodIndex];

			row.forEach((cell, colIndex) => {
				const variable = selectedVariables[colIndex];
				if (!variable) return;

				// Ignora células desabilitadas para este mês/variável
				if (!isMonthEnabledForVariable(variable, period.id)) {
					return;
				}

				if (
					cell.value !== "" &&
					cell.value !== null &&
					cell.value !== undefined
				) {
					if (!updatedData[variable.id]) {
						updatedData[variable.id] = {};
					}
					updatedData[variable.id][period.id] = cell.value;
				} else if (
					updatedData[variable.id] &&
					updatedData[variable.id][period.id]
				) {
					delete updatedData[variable.id][period.id];
					if (Object.keys(updatedData[variable.id]).length === 0) {
						delete updatedData[variable.id];
					}
				}
			});
		});

		setVariablesData(updatedData);
	};

	if (!temPermissaoVisualizar) return null;

	return (
		<Loading isLoading={isLoading}>
			<React.Fragment>
				{openModal && (
					<Launch
						show={openModal}
						onCloseClick={handleCloseLauch}
						updateData={() => getItemsDashboard({ 
							year: currentYear,
							organization_id: currentOrganizationStructure,
							name: valueName,
						})}
						currentYear={currentYear}
						currentLaunchId={currentLaunchId}
					/>
				)}

				{isVariablesModalOpen && (
					<Modal
						isOpen={isVariablesModalOpen}
						toggle={handleCloseVariablesModal}
						size="xl"
						centered
						className="variables-launch-modal"
						style={{ minWidth: "90vw", maxWidth: "90vw" }}
					>
						<style>
							{`
								.variables-launch-modal .modal-content { max-height: 90vh; display: flex; flex-direction: column; }
								.variables-launch-modal .modal-body { overflow-y: auto; min-height: 0; flex: 1 1 auto; }
								.variables-launch-modal-checkbox {
									appearance: none;
									-webkit-appearance: none;
									width: 18px;
									height: 18px;
									border: 2px solid #adb5bd;
									border-radius: 4px;
									vertical-align: middle;
									cursor: pointer;
								}
								.variables-launch-modal-checkbox:checked {
									background-color: #c18f00;
									border-color: #c18f00;
									background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E");
									background-size: 14px 14px;
									background-position: center;
									background-repeat: no-repeat;
								}
							`}
						</style>
						<div className="modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<div>
								<h5 style={{ marginBottom: 4 }}>Lançar Variáveis</h5>
								<div
									style={{
										fontSize: 13,
										color: "#6c757d",
									}}
								>
									Selecione as variáveis que deseja lançar valores
								</div>
							</div>
							<div>
								<Button
									type="button"
									style={{
										background: "none",
										padding: 10,
										color: "black",
										display: "flex",
										alignItems: "center",
										gap: 6,
									}}
									onClick={handleOpenCreateVariableModal}
								>
									<span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
									Cadastrar Variável
								</Button>
							</div>
						</div>
						<ModalBody
							className="variables-launch-modal-body"
							style={{ paddingTop: 16 }}
						>
							{isVariablesModalLoading ? (
								<div
									style={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										padding: "24px 0",
										gap: 8,
									}}
								>
									<span
										className="spinner-border spinner-border-sm"
										style={{ marginRight: 10 }}
									></span>
									Carregando...
								</div>
							) : variables.length === 0 ? (
								<div
									style={{
										fontSize: 13,
										color: "#6c757d",
										textAlign: "center",
										padding: "8px 0",
									}}
								>
									Nenhuma variável cadastrada.
								</div>
							) : (
								<>
									<div
										style={{
											display: "flex",
											justifyContent: "flex-end",
											alignItems: "center",
											gap: 8,
											marginBottom: 12,
										}}
									>
										<span
											style={{
												fontSize: 12,
												color: "#6c757d",
												whiteSpace: "nowrap",
											}}
										>
											Ordenar por
										</span>
										<div style={{ minWidth: 180 }}>
											<Select
												styles={{
													...customStyles,
													menuPortal: (base) => ({
														...base,
														zIndex: 10050,
													}),
													menu: (base) => ({
														...base,
														zIndex: 10050,
													}),
												}}
												options={VARIABLE_SORT_OPTIONS}
												value={
													VARIABLE_SORT_OPTIONS.find(
														(o) =>
															o.value === variablesSortBy,
													) || VARIABLE_SORT_OPTIONS[0]
												}
												onChange={(opt) =>
													setVariablesSortBy(
														opt?.value || "name_asc",
													)
												}
												isSearchable={false}
												menuPortalTarget={document.body}
												menuPosition="fixed"
											/>
										</div>
									</div>
									<div style={{ marginBottom: 12 }}>
										<Row>
											{sortedVariables.map((variable) => {
												const isSelected = selectedVariableIds.includes(
													variable.id,
												);

												return (
													<Col
														key={variable.id}
														md={3}
														sm={4}
														xs={6}
														className="mb-1"
													>
														<button
															type="button"
															onClick={() =>
																toggleVariableSelection(variable.id)
															}
															style={{
																width: "100%",
																borderRadius: 8,
																border: isSelected
																	? "1px solid #c18f00"
																	: "1px solid #dee2e6",
																backgroundColor: isSelected
																	? "rgba(193, 143, 0, 0.08)"
																	: "#fff",
																padding: "6px 10px",
																textAlign: "left",
																cursor: "pointer",
															}}
														>
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: 8,
															}}
														>
															<div style={{ flex: 1, minWidth: 0 }}>
																<div
																	style={{
																		display: "flex",
																		alignItems: "center",
																		marginBottom: 4,
																	}}
																>
																	<input
																		type="checkbox"
																		className="variables-launch-modal-checkbox"
																		checked={isSelected}
																		readOnly
																		style={{ marginRight: 8 }}
																	/>
																	<div
																		style={{
																			fontSize: 13,
																			fontWeight: 600,
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																		}}
																		title={variable.name}
																	>
																		{variable.name}
																	</div>
																</div>
																<div
																	style={{
																		fontSize: 12,
																		color: "#6c757d",
																	}}
																>
																	{variable.quantifier || "Nenhum"} •{" "}
																	{variable.periodicity}
																</div>
															</div>
															<div
																style={{
																	display: "inline-flex",
																	alignItems: "center",
																	gap: 2,
																	flexShrink: 0,
																}}
															>
																<span
																	role="button"
																	aria-label="Editar variável"
																	title="Editar variável"
																	onClick={(e) => {
																		e.preventDefault();
																		e.stopPropagation();
																		handleEditVariable(variable);
																	}}
																	style={{
																		display: "inline-flex",
																		alignItems: "center",
																		justifyContent: "center",
																		padding: 4,
																		color: "#000",
																		cursor: "pointer",
																		borderRadius: 4,
																	}}
																>
																	<Pencil size={16} />
																</span>
																<span
																	role="button"
																	aria-label="Excluir variável"
																	title="Excluir variável"
																	onClick={(e) => {
																		e.preventDefault();
																		e.stopPropagation();
																		handleDeleteVariable(variable);
																	}}
																	style={{
																		display: "inline-flex",
																		alignItems: "center",
																		justifyContent: "center",
																		padding: 4,
																		color: "#000",
																		cursor: "pointer",
																		borderRadius: 4,
																	}}
																>
																	<Trash size={16} />
																</span>
															</div>
														</div>
														</button>
													</Col>
												);
											})}
										</Row>
									</div>

									{selectedVariables.length > 0 && (
										<div
											style={{
												width: "100%",
												borderTop: "1px solid #dee2e6",
												paddingTop: 12,
											}}
										>
											<div
												ref={variablesSpreadsheetRef}
												className="variables-modal-spreadsheet-wrapper"
												style={{ width: "100%" }}
											>
												<style>
													{`
														.variables-modal-spreadsheet-wrapper .Spreadsheet { width: 100%; --outline-color: #c18f00; --outline-background-color: rgba(193, 143, 0, 0.15); }
														.variables-modal-spreadsheet-wrapper .Spreadsheet__table { width: 100% !important; table-layout: fixed; }
														.variables-modal-spreadsheet-wrapper .Spreadsheet__table th:first-child,
														.variables-modal-spreadsheet-wrapper .Spreadsheet__table td:first-child {
															width: 64px !important;
															max-width: 64px !important;
															min-width: 64px !important;
															padding: 4px 8px !important;
															text-align: center !important;
														}
														.variables-modal-spreadsheet-wrapper .Spreadsheet__header { white-space: pre-line; word-wrap: break-word; }
														.variables-modal-spreadsheet-wrapper .Spreadsheet__cell--readonly {
															background-color: #f3f4f6;
															color: #6b7280;
															text-align: center;
														}
													`}
												</style>
												<Spreadsheet
													data={spreadsheetDataVariables}
													columnLabels={columnLabelsVariables}
													rowLabels={rowLabelsVariables}
													onChange={handleSpreadsheetVariablesChange}
													onSelect={handleVariablesSelect}
												/>
											</div>
										</div>
									)}
								</>
							)}
						</ModalBody>
						<ModalFooter>
							<div
								style={{
									display: "flex",
									justifyContent: "flex-end",
									alignItems: "center",
									gap: 8,
									width: "100%",
								}}
							>
								<Button
									type="button"
									style={{
										background: "none",
										padding: 10,
										color: "black",
									}}
										onClick={async () => {
											try {
												const payloadValues = {};

												selectedVariables.forEach((variable) => {
													periodRows.forEach((period) => {
														if (!isMonthEnabledForVariable(variable, period.id)) {
															return;
														}

														const valueRaw =
															variablesData[variable.id]?.[period.id];
														if (
															valueRaw === undefined ||
															valueRaw === null ||
															valueRaw === ""
														) {
															return;
														}

														if (!payloadValues[variable.id]) {
															payloadValues[variable.id] = {};
														}

														// month passa a representar diretamente o índice do período
														payloadValues[variable.id][period.id] = valueRaw;
													});
												});

												await indicadoresService.updateVariableLaunch({
													year: currentYear,
													values: payloadValues,
												});

												SendaCustomSwal.fire({
													iconHtml: '<i class="success-icon"></i>',
													title: "Sucesso",
													text: "Valores das variáveis salvos com sucesso!",
													customClass: {
														confirmButton: "custom-confirm-button",
													},
												});
											} catch (error) {
												console.error("Erro ao salvar valores das variáveis:", error);
												SendaCustomSwal.fire({
													iconHtml: '<i class="error-icon"></i>',
													title: "Erro",
													text: "Não foi possível salvar os valores das variáveis.",
													customClass: {
														confirmButton: "custom-confirm-button",
													},
												});
											}
										}}
								>
									<FloppyDisk size={16} /> Salvar
								</Button>
								<Button
									type="button"
									style={{
										background: "none",
										padding: 10,
										color: "black",
									}}
									onClick={handleCloseVariablesModal}
								>
									<SignOut size={16} /> Fechar
								</Button>
							</div>
						</ModalFooter>
					</Modal>
				)}

				{isCreateVariableModalOpen && (
					<Modal
						isOpen={isCreateVariableModalOpen}
						toggle={handleCloseCreateVariableModal}
						size="lg"
						style={{ minWidth: "720px" }}
						centered
					>
						<div
							className="modal-header"
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<h5 style={{ marginBottom: 0 }}>
								{editingVariable
									? `Editar Variável: ${editingVariable.name}`
									: "Cadastrar Variável"}
							</h5>
						</div>
						<ModalBody>
							<div style={{ paddingTop: 8 }}>
								<Row>
									<Col md={8}>
										<FormGroup className="mb-3">
											<Label for="variable-name">Nome da Variável</Label>
											<Input
												id="variable-name"
												name="variable-name"
												type="text"
												placeholder="Digite o nome da variável"
												value={createVariableName}
												onChange={(e) => setCreateVariableName(e.target.value)}
											/>
										</FormGroup>
									</Col>
									<Col md={4}>
										<FormGroup className="mb-3">
											<Label for="variable-quantifier">
												Quantificador
											</Label>
											<Select
												inputId="variable-quantifier"
												styles={customStyles}
												placeholder="Selecione"
												value={createVariableQuantifier}
												onChange={setCreateVariableQuantifier}
												options={VARIABLE_QUANTIFIER_OPTIONS}
											/>
										</FormGroup>
									</Col>
								</Row>
								<Row>
									<Col md={8}>
										<FormGroup className="mb-3">
											<Label for="variable-periodicity">
												Periodicidade da Variável
											</Label>
											<Select
												inputId="variable-periodicity"
												styles={customStyles}
												placeholder="Selecione a periodicidade"
												value={createVariablePeriodicity}
												onChange={setCreateVariablePeriodicity}
												options={VARIABLE_PERIODICITY_OPTIONS}
											/>
										</FormGroup>
									</Col>
									<Col md={4}>
										<FormGroup className="mb-3">
											<Label for="variable-decimals">
												Casas Decimais
											</Label>
											<Select
												inputId="variable-decimals"
												styles={customStyles}
												placeholder="Selecione"
												value={createVariableDecimals}
												onChange={setCreateVariableDecimals}
												options={VARIABLE_DECIMALS_OPTIONS}
											/>
										</FormGroup>
									</Col>
								</Row>
							</div>
						</ModalBody>
						<ModalFooter>
							<div
								style={{
									display: "flex",
									justifyContent: "flex-end",
									alignItems: "center",
									gap: 8,
									width: "100%",
								}}
							>
								<Button
									type="button"
									style={{
										background: "none",
										padding: 10,
										color: "black",
									}}
									disabled={isCreateVariableSaving}
									onClick={async () => {
										if (!createVariableName || !createVariablePeriodicity) {
											SendaCustomSwal.fire({
												iconHtml: '<i class="error-icon"></i>',
												title: "Atenção",
												text: "Preencha o nome e a periodicidade da variável.",
												customClass: {
													confirmButton: "custom-confirm-button",
												},
											});
											return;
										}

										setIsCreateVariableSaving(true);
										try {
											const payload = {
												name: createVariableName,
												quantifier: createVariableQuantifier
													? createVariableQuantifier.value || null
													: null,
												periodicity: createVariablePeriodicity.value,
												decimals_cases:
													createVariableDecimals?.value ?? 2,
											};

											if (editingVariable) {
												const updated = await indicadoresService.updateVariable(
													editingVariable.id,
													payload,
												);
												setVariables((prev) =>
													prev.map((v) =>
														v.id === editingVariable.id ? updated : v,
													),
												);
												refreshDashboard();
											} else {
												const created = await indicadoresService.createVariable(
													payload,
												);
												setVariables((prev) => [...prev, created]);
											}

											handleCloseCreateVariableModal();
										} catch (error) {
											console.error(
												editingVariable
													? "Erro ao atualizar variável:"
													: "Erro ao criar variável:",
												error,
											);
											SendaCustomSwal.fire({
												iconHtml: '<i class="error-icon"></i>',
												title: "Atenção",
												text:
													error?.response?.data?.message ||
													(editingVariable
														? "Não foi possível atualizar a variável."
														: "Não foi possível salvar a variável."),
												customClass: {
													confirmButton: "custom-confirm-button",
												},
											});
										} finally {
											setIsCreateVariableSaving(false);
										}
									}}
								>
									{isCreateVariableSaving ? (
										<span
											className="spinner-border spinner-border-sm mr-1"
											style={{ marginRight: 10 }}
										></span>
									) : null}
									<FloppyDisk size={16} /> Salvar
								</Button>
								<Button
									type="button"
									style={{
										background: "none",
										padding: 10,
										color: "black",
									}}
									onClick={handleCloseCreateVariableModal}
								>
									<SignOut size={16} /> Fechar
								</Button>
							</div>
						</ModalFooter>
					</Modal>
				)}

				{isIndicadorModalOpen && (
					<Modal
						isOpen={isIndicadorModalOpen}
						toggle={handleCloseIndicadorModal}
						size="xl"
						centered
						className="indicador-form-modal"
						style={{ minWidth: "90vw", maxWidth: "90vw" }}
					>
						<style>
							{`
								.indicador-form-modal .modal-content { max-height: 90vh; display: flex; flex-direction: column; }
								.indicador-form-modal .modal-body { overflow-y: auto; min-height: 0; flex: 1 1 auto; }
							`}
						</style>
						<div
							className="modal-header"
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<h5 style={{ marginBottom: 0 }}>
								{editingIndicadorId
									? "Editar Indicador"
									: "Adicionar Indicador"}
							</h5>
						</div>
						<ModalBody>
							<IndicadorFormContent
								id={editingIndicadorId}
								onClose={handleCloseIndicadorModal}
								onSaved={handleIndicadorSaved}
								isModal={true}
								t={props.t}
							/>
						</ModalBody>
					</Modal>
				)}

				<div className="page-content">
					<Container fluid>
						{temPermissaoAdicionar && (
							<Breadcrumbs
								title={props.t("Indicadores")}
								onBotaoClick={() =>
									isV2Enabled
										? handleOpenIndicadorModal()
										: navigate("/indicadores/form")
								}
								botao={"Adicionar Indicador"}
								extraButton={
									isV2Enabled
										? {
												label: "Lançar variáveis",
												icon: "bx bx-slider-alt",
												onClick: handleOpenVariablesModal,
										  }
										: undefined
								}
								extraButtonSecondary={{
									label: "Alternar visualização",
									icon: "bx bx-align-middle",
									onClick: () =>
										toggleViewMode(viewMode, setViewMode),
								}}
							/>
						)}
						<div
							style={{
								backgroundColor: "white",
								padding: 20,
								borderRadius: 10,
								marginTop: 20,
								marginBottom: 20,
							}}
						>
							<Row>
								<Col sm={5} className="p-3">
									<FormGroup className="mb-3">
										<Label>Nome</Label>
										<Input
											name="nome"
											placeholder="Nome"
											type="text"
											className="form-control"
											id="validationCustom01"
											onChange={(e) => {
												setValueName(e.target.value);
											}}
											value={valueName}
										/>
									</FormGroup>
								</Col>
								<Col sm={3} className="p-3">
									<FormGroup className="mb-3">
										<Label for="selectEstruturaOrg">
											Departamento
										</Label>
										<Input
											type="select"
											name="select"
											id="selectEstruturaOrg"
											value={currentOrganizationStructure}
											onChange={(e) =>
												setCurrentOrganizationStructure(
													e.target.value,
												)
											}
										>
											<option value="">Todas</option>
											{organizationStructures.map(
												(option) => (
													<option
														key={option.value}
														value={option.value}
													>
														{option.label}
													</option>
												),
											)}
										</Input>
									</FormGroup>
								</Col>
								<Col sm={3} className="p-3">
									<FormGroup className="mb-3">
										<Label for="selectAno">Ano</Label>
										<Input
											type="select"
											name="select"
											id="selectAno"
											value={currentYear}
											onChange={(e) =>
												setCurrentYear(e.target.value)
											}
										>
											{years.map((option) => (
												<option
													key={option.value}
													value={option.value}
												>
													{option.label}
												</option>
											))}
										</Input>
									</FormGroup>
								</Col>
								<Col
									sm={1}
									className="p-3"
									style={{ alignSelf: "center" }}
								>
									<Button
										onClick={handleFilterInfos}
										disabled={isFiltering || isLoading2}
										style={{
											marginTop: 10,
											background: "none",
											color: "black",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: "2px",
										}}
									>
										<Funnel size={16} /> Filtrar
									</Button>
								</Col>
							</Row>
						</div>
						{isLoading2 ? (
							<div
								style={{
									display: "flex",
									justifyContent: "center",
									height: "50%",
								}}
							>
								<span className="spinner spinner-slow"></span>
							</div>
						) : (
							<Row>
								{dashboards?.map((dashboard) => (
									<Col key={dashboard?.id} lg={viewMode}>
										<Multiple
											temPermissaoEditar={
												temPermissaoEditar
											}
											temPermissaoLancar={
												temPermissaoLancar
											}
											goal={dashboard}
											handleOpenLaunch={() =>
												handleOpenLaunch(dashboard?.id)
											}
											paramsBusca={{
												organization_id:
													currentOrganizationStructure,
												name: valueName,
											}}
											currentYear={currentYear}
											onEdit={(id) =>
												handleOpenIndicadorModal(id)
											}
										/>
									</Col>
								))}
							</Row>
						)}
					</Container>
				</div>
			</React.Fragment>
		</Loading>
	);
};

export default withTranslation()(Indicadores);
